import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { TextField, InputAdornment, IconButton, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PageHeaderAddButton,
	PageHeaderListActions,
	Pagination,
	PaginationHeader,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import CustomerForm from "./customer-add-edit-container";
import CustomerListing from "./customer-listing";
import { getPageResults, useTerminology } from "utils";
import { INFO_HOME_ROUTE } from "features/home/home-routes";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	}
}));

const Customers = ({
	role,
	onAdd,
	onEdit,
	onDelete,
	getCustomerDetails,
	onGetCustomersList,
	pagination,
}) => {
	const classes = useStyles();
	const { customersLabel } = useTerminology();
	const customerList = [...getPageResults(pagination)].sort((a, b) => {
		const aActive = String(a.status).toLowerCase() === "active";
		const bActive = String(b.status).toLowerCase() === "active";
		return (bActive ? 1 : 0) - (aActive ? 1 : 0);
	});
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			onGetCustomersList({
				page: 1,
				pageSize: 20,
				orderBy: "status,customerName",
				ascending: true,
				searchKey: searchFilter
			});
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter, onGetCustomersList]);

	return (
		<Page className={classes.root} title={customersLabel}>
			<ScrollToTopOnMount />
				<PageHeaderAddButton
					title={customersLabel}
					addTabTitle="New"
					onAdd={onAdd}
					staffUserHide={true}
					backActionPath={INFO_HOME_ROUTE}
					titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}
				>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
								onClick={() => {
																setSearchFilter("");
																onGetCustomersList({
																	page: 1,
																	pageSize: 20,
																	orderBy: "status,customerName",
																	ascending: true,
																	searchKey: ""
																});
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={`e.g. ${customersLabel.replace(/s$/, "")} Name, Contact Person, Email Address, Status`}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderAddButton>

			<PageContent pad>
				<div className={classes.body}>
					<PaginationHeader hidePagination pagination={pagination} />

					{customerList.map((node, index) => (
						<CustomerListing
							key={index}
							role={role || "Unknown"}
							customer={node}
							onEdit={onEdit}
							onDelete={onDelete}
							onSelect={getCustomerDetails}
						/>
					))}
				</div>

				<Pagination
					hidePagination
					onGetList={onGetCustomersList}
					pagination={pagination}
					orderBy="status,customerName"
					searchKey={searchFilter}
				/>
			</PageContent>
			<CustomerForm />
		</Page>
	);
};

Customers.propTypes = {
	role: PropTypes.string.isRequired,
	onAdd: PropTypes.func,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	getCustomerDetails: PropTypes.func.isRequired,
	onGetCustomersList: PropTypes.func.isRequired,
	pagination: PropTypes.object.isRequired,
};

export default Customers;
